import { ReactNode, useState } from "react";
import { Check } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_BRANDING } from "@/types/branding";

const genieBrandColor =(agent: Agent) => agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;

const hasCustomBranding = (agent: Agent) =>
  Boolean(
    agent.branding &&
      (agent.branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
        agent.branding.secondaryColor ||
        agent.branding.customAvatarUrl ||
        agent.branding.gradientEnabled),
  );

/** The genie's avatar, or the brand "All" disc when no genie is picked. */
export const GenieFilterAvatar = ({ agent, size }: { agent: Agent | null; size: number }) =>
  agent ? (
    <span className="flex shrink-0 overflow-hidden rounded-full" style={{ height: size, width: size }}>
      <GenieAvatar
        size={size}
        color={genieBrandColor(agent)}
        secondaryColor={agent.branding?.secondaryColor}
        gradientEnabled={agent.branding?.gradientEnabled}
        gradientAngle={agent.branding?.gradientAngle}
        avatarBackgroundOpacity={agent.branding?.avatarBackgroundOpacity}
        customAvatarUrl={agent.branding?.logoUrl || agent.branding?.customAvatarUrl}
        hasCustomBranding={hasCustomBranding(agent)}
      />
    </span>
  ) : (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] font-bold text-white"
      style={{ height: size, width: size, fontSize: Math.max(8, Math.round(size / 3.2)) }}
    >
      All
    </span>
  );

interface GenieFilterPickerProps {
  agents: Agent[];
  value: string | null;
  onChange: (agentId: string | null) => void;
  showOwner?: boolean;
  /** The button that opens the picker. */
  children: ReactNode;
  onOpenChange?: (open: boolean) => void;
}

/** Searchable genie list for conversation filters, opened from any trigger. */
export const GenieFilterPicker = ({ agents, value, onChange, showOwner = false, children, onOpenChange }: GenieFilterPickerProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const pick = (agentId: string | null) => {
    onChange(agentId);
    changeOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[min(26rem,calc(100vw-2rem))] p-0">
        <Command>
          {agents.length > 6 && <CommandInput placeholder="Search genies..." className="text-sm" />}
          <CommandList className="max-h-80">
            <CommandEmpty>No genies found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="all genies" onSelect={() => pick(null)} className="flex items-center gap-3 cursor-pointer">
                <GenieFilterAvatar agent={null} size={32} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900">All Genies</div>
                  <div className="text-xs text-gray-500">Every conversation in your inbox</div>
                </div>
                {!value && <Check className="h-4 w-4 text-genie-primary" />}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Your Genies">
              {agents.map((agent) => {
                const brand = genieBrandColor(agent);
                const isSelected = value === agent.id;

                return (
                  <CommandItem
                    key={agent.id}
                    value={`${agent.name} ${agent.id}`}
                    onSelect={() => pick(agent.id)}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <span className="flex shrink-0 overflow-hidden rounded-full border" style={{ borderColor: `${brand}66` }}>
                      <GenieFilterAvatar agent={agent} size={32} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold" style={{ color: isSelected ? brand : "#111827" }}>
                        {agent.name}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        {showOwner && agent.user_id !== user?.id ? "Team genie" : agent.description || "Genie conversations"}
                      </div>
                    </div>
                    {isSelected && <Check className="h-4 w-4" style={{ color: brand }} />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
